import { create } from "zustand";

export const isMockConfigAvailable = import.meta.env.DEV;

export type MockApiKey = "products" | "billing" | "senderDomain";

const STORAGE_KEY = "connect-ui:mock-config";

type MockConfigState = Record<MockApiKey, boolean>;

const ALL_KEYS: MockApiKey[] = ["products", "billing", "senderDomain"];

const DEFAULT_STATE: MockConfigState = {
  products: false,
  billing: false,
  senderDomain: false,
};

function readInitial(): MockConfigState {
  if (!isMockConfigAvailable) return DEFAULT_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<MockConfigState>;
    return Object.fromEntries(
      ALL_KEYS.map((key) => [key, parsed[key] ?? false]),
    ) as MockConfigState;
  } catch {
    return DEFAULT_STATE;
  }
}

interface MockConfigStore extends MockConfigState {
  setApiMock: (key: MockApiKey, enabled: boolean) => void;
}

export const useMockConfigStore = create<MockConfigStore>((set) => ({
  ...readInitial(),
  setApiMock: (key, enabled) => {
    if (isMockConfigAvailable) {
      try {
        const current = useMockConfigStore.getState();
        const next = { ...current, [key]: enabled };
        const stateToSave = Object.fromEntries(
          ALL_KEYS.map((k) => [k, next[k]]),
        );
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
      } catch {
        /* ignore quota / disabled storage */
      }
    }
    set({ [key]: enabled });
  },
}));

/** Safe to call outside React — reads current store state at call time. */
export function isMock(key: MockApiKey): boolean {
  if (!isMockConfigAvailable) return false;
  return useMockConfigStore.getState()[key];
}
